import { useState, useEffect, useCallback, useRef } from "react";
import Storage from "../../services/Storage";
import "./Advert.scss";
import { Bling as GPT } from "react-gpt";
import { urlConstants } from "../../utils/urlConstants";
import { useSelector } from "react-redux";
import { Roles } from "../../utils/Roles";

GPT.enableSingleRequest();

const AD_ONLY_HEADER_SELECTOR = [
  ".college-link-header",
  ".personal-notes-header",
  ".interest-header",
  ".student-calendar-header",
  ".student-checklist-header",
  ".portfolio-ad-header",
  ".reflection-header",
  ".registration-ad-header",
  ".student-message-header",
  ".ladder-ad-header",
  ".message-ad-header",
  ".curriculum-ad-header",
  ".portfolio-header",
  ".passion-ad-header",
  ".counselor-calendar-header",
].join(", ");

const getSlotHeight = (width) => {
  if (width >= 1200) return 250;
  if (width >= 992) return 110;
  if (width >= 768) return 90;
  if (width >= 480) return 60;
  return 50;
};

function Advert() {
  const { API_URL } = urlConstants;
  const token = Storage.getTokenInCookie("token");
  const userData = useSelector((state) => state.UserReducer.userObj);
  const wrapperRef = useRef(null);
  const failedPathsRef = useRef(new Set());
  const loadTimeoutRef = useRef(null);

  const adDivId = "ad";
  const [radioValue, setRadioValue] = useState(1);
  const [adPath, setAdPath] = useState([]);
  const [showAdv, setShowAdv] = useState(false);
  const [adFilled, setAdFilled] = useState(false);
  const [count, setCount] = useState(0);

  const updateAdScale = useCallback(() => {
    const width = window.innerWidth;
    const slotHeight = getSlotHeight(width);
    document.documentElement.style.setProperty("--ad-slot-height", `${slotHeight}px`);

    document.querySelectorAll(".advert-wrapper.is-ad-loaded").forEach((wrapper) => {
      const container =
        wrapper.closest(
          ".ad-section-centered, [class*='-header'], .topSectionWrap, .googleAddWrap"
        ) || wrapper.parentElement;
      const availableWidth = container?.clientWidth || wrapper.clientWidth || width;
      const scale = Math.min(1, Math.max(0.32, availableWidth / 970));
      wrapper.style.setProperty("--ad-scale", String(scale));
    });
  }, []);

  const syncHostVisibility = useCallback((filled, pending = false) => {
    const host = wrapperRef.current;
    if (!host) return;

    const mark = (el, { allowHideHeader = true } = {}) => {
      if (!el) return;
      el.classList.toggle("has-ad-loaded", !!filled);
      // Never mark empty while a request is still pending (keeps GPT alive)
      el.classList.toggle("ad-slot-empty", !filled && !pending && allowHideHeader);
    };

    mark(host.closest(".ad-section-centered, .googleAddWrap"));
    mark(host.closest(AD_ONLY_HEADER_SELECTOR));

    // Mixed headers (ad + profile/hello) — never hide the whole block
    const mixedHeader = host.closest(
      ".student-goal-header, .topSectionWrap, .dashboard-header"
    );
    if (mixedHeader) {
      mixedHeader.classList.toggle("has-ad-loaded", !!filled);
      mixedHeader.classList.remove("ad-slot-empty");
    }
  }, []);

  const hideAdsCompletely = useCallback(() => {
    setAdFilled(false);
    setShowAdv(false);
    setAdPath([]);
  }, []);

  const tryNextPathOrHide = useCallback(
    (failedIndex) => {
      failedPathsRef.current.add(failedIndex);
      const total = adPath.length;
      if (total === 0 || failedPathsRef.current.size >= total) {
        hideAdsCompletely();
        return;
      }
      setAdFilled(false);
      syncHostVisibility(false, true);
      setCount((prev) => {
        let next = (prev + 1) % total;
        let guard = 0;
        while (failedPathsRef.current.has(next) && guard < total) {
          next = (next + 1) % total;
          guard += 1;
        }
        return next;
      });
    },
    [adPath.length, hideAdsCompletely, syncHostVisibility]
  );

  useEffect(() => {
    let googleAddApiUrl = "";
    if (userData?.role === Roles.STUDENT_NAME || userData?.role === Roles.PARENT_NAME) {
      googleAddApiUrl = `${API_URL}/api/student/googleads/`;
    }

    if (!googleAddApiUrl) {
      hideAdsCompletely();
      return undefined;
    }

    let cancelled = false;
    fetch(googleAddApiUrl, {
      method: "GET",
      headers: { "x-authorization": `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.adv_detail)
          ? data.adv_detail
              .filter((item) => item?.status === "active" && item?.path)
              .map((item) => item.path)
          : [];
        failedPathsRef.current = new Set();
        setAdPath(list);
        setShowAdv(list.length > 0);
        setAdFilled(false);
        setCount(0);
      })
      .catch(() => {
        if (!cancelled) hideAdsCompletely();
      });

    return () => {
      cancelled = true;
    };
  }, [API_URL, token, userData?.role, hideAdsCompletely]);

  useEffect(() => {
    updateAdScale();
    window.addEventListener("resize", updateAdScale);
    return () => window.removeEventListener("resize", updateAdScale);
  }, [updateAdScale]);

  useEffect(() => {
    if (!showAdv || !adFilled) return undefined;

    updateAdScale();
    const observer = new ResizeObserver(updateAdScale);
    document.querySelectorAll(".advert-wrapper.is-ad-loaded").forEach((el) =>
      observer.observe(el)
    );

    return () => observer.disconnect();
  }, [showAdv, adFilled, adPath.length, updateAdScale]);

  useEffect(() => {
    setRadioValue(`ad${count + 1}`);
  }, [count]);

  // Rotate only among successfully fillable slots once an ad is visible
  useEffect(() => {
    if (!adFilled || !adPath || adPath.length <= 1) return undefined;
    const interval = setInterval(() => {
      setCount((prev) => (prev + 1) % adPath.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [adFilled, adPath.length]);

  useEffect(() => {
    if (count >= adPath.length && adPath.length > 0) {
      setCount(0);
    }
  }, [adPath.length, count]);

  // If GPT never responds, don't leave an empty blue box
  useEffect(() => {
    if (!showAdv || adFilled) {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      return undefined;
    }

    loadTimeoutRef.current = setTimeout(() => {
      tryNextPathOrHide(count);
    }, 6000);

    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };
  }, [showAdv, adFilled, count, tryNextPathOrHide]);

  useEffect(() => {
    if (!showAdv) return;
    syncHostVisibility(adFilled, !adFilled);
  }, [showAdv, adFilled, count, syncHostVisibility]);

  const handleSlotRenderEnded = useCallback(
    (event) => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }

      if (event?.isEmpty) {
        tryNextPathOrHide(count);
        return;
      }

      failedPathsRef.current.delete(count);
      setAdFilled(true);
      syncHostVisibility(true, false);
      requestAnimationFrame(() => updateAdScale());
    },
    [count, syncHostVisibility, tryNextPathOrHide, updateAdScale]
  );

  // No inventory / all slots empty — leave a marker so host blue boxes collapse
  if (!showAdv) {
    return (
      <span
        ref={(el) => {
          if (!el) return;
          const section = el.closest(".ad-section-centered, .googleAddWrap");
          section?.classList.add("ad-slot-empty");
          section?.classList.remove("has-ad-loaded");
          const adOnlyHeader = el.closest(AD_ONLY_HEADER_SELECTOR);
          adOnlyHeader?.classList.add("ad-slot-empty");
          adOnlyHeader?.classList.remove("has-ad-loaded");
        }}
        className="advert-absent"
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={`advert-wrapper ${adFilled ? "is-ad-loaded" : "is-ad-pending"}`}
      aria-hidden={!adFilled}
    >
      <div id={adDivId} className="advSlider adSize">
        {adPath[count] && (
          <GPT
            key={adPath[count]}
            adUnitPath={adPath[count]}
            slotSize={[970, 250]}
            collapseEmptyDiv
            renderWhenViewable={false}
            sizeMapping={[
              { viewport: [0, 0], slot: [320, 50] },
              { viewport: [480, 0], slot: [468, 60] },
              { viewport: [768, 0], slot: [728, 90] },
              { viewport: [992, 0], slot: [970, 250] },
            ]}
            onSlotRenderEnded={handleSlotRenderEnded}
          />
        )}
      </div>
      {adFilled && adPath.length > 1 && (
        <div className="ad-dots-container">
          {adPath.map((_, i) => (
            <label key={i} className="ad-dot-label">
              <input
                type="radio"
                name="adDots"
                value={i + 1}
                checked={radioValue === `ad${i + 1}`}
                onChange={() => {
                  setRadioValue(`ad${i + 1}`);
                  setCount(i);
                }}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default Advert;
